import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Field, change, getFormValues } from "@mds/common/components/forms/form";
import { Col, Row, Typography, Alert } from "antd";
import RenderField from "@mds/common/components/forms/RenderField";
import RenderRadioButtons from "@mds/common/components/forms/RenderRadioButtons";
import RenderSelect from "@mds/common/components/forms/RenderSelect";
import {
  email,
  maxLength,
  phoneNumber,
  postalCodeWithCountry,
  required,
  requiredRadioButton,
} from "@mds/common/redux/utils/Validate";
import { getDropdownProvinceOptions } from "@mds/common/redux/selectors/staticContentSelectors";
import { IOrgbookCredential } from "@mds/common/interfaces/party";
import { CONTACTS_COUNTRY_OPTIONS } from "@mds/common/constants/strings";
import RenderBCRegistrationSearch from "@mds/common/components/forms/RenderBCRegistrationSearch";
import {
  fetchOrgBookCredential,
} from "@mds/common/redux/actionCreators/orgbookActionCreator";
import { getOrgBookCredential } from "@mds/common/redux/selectors/orgbookSelectors";
import { normalizePhone } from "@mds/common/redux/utils/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleX, faSpinner } from "@fortawesome/pro-light-svg-icons";
import { IProjectSummaryForm } from "@mds/common/interfaces";
import { ProjectSummaryFormComponentProps } from "./ProjectSummaryForm";
import { FORM } from "@mds/common/constants/forms";
import { COLOR } from "@mds/common/constants/styles";
import { useAppDispatch } from "@mds/common/redux/rootState";

export const Agent: FC<ProjectSummaryFormComponentProps> = ({ fieldsDisabled }) => {
  const dispatch = useAppDispatch();
  const formValues = useSelector(getFormValues(FORM.ADD_EDIT_PROJECT_SUMMARY)) as IProjectSummaryForm;
  const { agent, is_agent = false } = formValues;
  const { party_type_code, address, credential_id } = agent ?? {};
  const { address_type_code, sub_division_code } = address ?? {};
  const isInternational = address_type_code === "INT";
  // currently no endpoints, etc, for address_type_code
  const provinceOptions = useSelector(getDropdownProvinceOptions);
  const [orgBookOptions, setOrgBookOptions] = useState([]);
  const [credential, setCredential] = useState<IOrgbookCredential>(null);
  const orgBookCredential = useSelector(getOrgBookCredential);
  const [verified, setVerified] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [verifiedCredential, setVerifiedCredential] = useState(null);

  useEffect(() => {
    setCheckingStatus(true);
    setOrgBookOptions([]);
    setCredential(null);
    setVerified(false);
    setVerifiedCredential(null);
    dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.party_bc_registration", null));
    if (credential_id) dispatch(fetchOrgBookCredential(credential_id));
  }, [credential_id]);

  useEffect(() => {
    if (credential_id && orgBookCredential?.topic?.local_name?.text) {
      setCredential(orgBookCredential);
      const options = [{ text: orgBookCredential.topic.local_name.text, value: credential_id }];
      setOrgBookOptions(options);
    }
  }, [orgBookCredential]);

  useEffect(() => {
    // set a value for party type code because required validation doesn't show
    if (!party_type_code && is_agent) {
      dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.party_type_code", "ORG"));
    } else if (!is_agent) {
      dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent", {}));
    }
  }, [is_agent]);

  useEffect(() => {
    setCredential(null);
    if (party_type_code === "ORG") {
      dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.first_name", null));
    }
  }, [party_type_code]);

  useEffect(() => {
    // clear out the province if country has changed and it no longer matchess
    const selectedProvince = sub_division_code
      ? provinceOptions.find((p) => p.value === sub_division_code)
      : {};
    if (address_type_code === "INT" || selectedProvince?.subType !== address_type_code) {
      dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.address.sub_division_code", null));
    }
  }, [address_type_code]);

  useEffect(() => {
    setVerified(false);
    setCheckingStatus(true);
    if (credential) {
      setVerified(true);
      setCheckingStatus(false);
      const payload = {
        businessNumber: credential.topic.source_id || "-",
        registrationStatus: credential.inactive ? "Inactive" : "Active",
        registriesId: credential.id,
      };
      setVerifiedCredential(payload);
      dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.credential_id", credential.id));
      const orgBookEntity = {
        registration_id: credential.topic.source_id,
        registration_status: !credential.inactive,
        registration_date: credential.attributes[0].value,
        name_id: credential.topic.local_name.id,
        name_text: credential.topic.local_name.text,
        credential_id: credential.topic.local_name.credential_id,
        company_alias: null,
      };
      dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.party_bc_registration", orgBookEntity));
    }
  }, [credential]);

  const renderStatus = () => {
    let icon = faSpinner;
    let color = undefined;
    let text = undefined;

    if (!checkingStatus) {
      if (verified) {
        icon = faCircleCheck;
        color = COLOR.successGreen;
        text = "Verified on BC Registries";
      } else {
        icon = faCircleX;
        color = "#D8292F";
        text = "Not found in BC Registries lookup";
      }
    }

    return (
      <Typography.Paragraph strong className="light">
        <FontAwesomeIcon
          size="lg"
          color={color}
          icon={icon}
          spin={checkingStatus}
          className="margin-medium--right"
        />
        {text}
      </Typography.Paragraph>
    );
  };

  const handleResetParty = () => {
    dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.party_name", null));
    dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.first_name", null));
    dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.middle_name", null));
    dispatch(change(FORM.ADD_EDIT_PROJECT_SUMMARY, "agent.party_bc_registration", null));
    setCredential(null);
    setOrgBookOptions(null);
    setVerifiedCredential(null);
  };

  return (
    <div className="ant-form-vertical">
      <Typography.Title level={3}>Representing Agent</Typography.Title>
      <Typography.Paragraph>
        The applicant may authorize a representing agent to deal with the Ministry directly on
        future aspects of this application. This section must be completed in full if a representing
        agent is a person who is not an employee of the applicant.
      </Typography.Paragraph>
      <Typography.Paragraph>
        <Alert
          description="This section will not identify the Agent as defined under the Mines Act, unless the Ministry of Mining and Critical Minerals is otherwise notified by the owner."
          type="warning"
          showIcon
        />
      </Typography.Paragraph>
      <Field
        name="is_agent"
        id="is_agent"
        required
        validate={[requiredRadioButton]}
        label="Are you an agent applying on behalf of the applicant?"
        component={RenderRadioButtons}
        disabled={fieldsDisabled}
      />

      {is_agent && (
        <>
          <Field
            name="agent.party_type_code"
            required
            component={RenderRadioButtons}
            defaultValue={"ORG"}
            customOptions={[
              { label: "Organization", value: "ORG" },
              { label: "Person", value: "PER" },
            ]}
            optionType="button"
            onChange={handleResetParty}
            disabled={fieldsDisabled}
          />
          {party_type_code === "ORG" && (
            <div>
              <Field
                name="agent.party_name"
                id="agent.party_name"
                required
                validate={[required]}
                label="Company Legal Name"
                setCredential={setCredential}
                data={orgBookOptions}
                help={"as registered with the BC Registrar of Companies"}
                component={RenderBCRegistrationSearch}
                disabled={fieldsDisabled}
              />
              {verifiedCredential && (
                <div className="table-summary-card">
                  {renderStatus()}

                  <Typography.Paragraph className="light margin-none">
                    Business Number: {verifiedCredential.businessNumber}
                  </Typography.Paragraph>

                  <Typography.Paragraph className="light margin-none">
                    BC Registries ID: {verifiedCredential.registriesId}
                  </Typography.Paragraph>
                  <Typography.Paragraph className="light margin-none">
                    BC Registration Status {verifiedCredential.registrationStatus}
                  </Typography.Paragraph>
                  <br />
                </div>
              )}
              <Row gutter={16}>
                <Col md={12} sm={24}>
                  <Field
                    id="agent.party_bc_registration.registration_id"
                    name="agent.party_bc_registration.registration_id"
                    label="Incorporation Number"
                    required
                    validate={[required, maxLength(25)]}
                    component={RenderField}
                    disabled={fieldsDisabled}
                  />
                </Col>
              </Row>
            </div>
          )}
          {party_type_code === "PER" && (
            <Row gutter={16}>
              <Col md={12} sm={24}>
                <Field
                  name="agent.first_name"
                  label="First Name"
                  component={RenderField}
                  required
                  validate={[required, maxLength(60)]}
                  disabled={fieldsDisabled}
                />
              </Col>
              <Col md={12} sm={24}>
                <Field
                  name="agent.party_name"
                  label="Last Name"
                  component={RenderField}
                  required
                  validate={[required, maxLength(60)]}
                  disabled={fieldsDisabled}
                />
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col md={12} sm={24}>
              <Field
                name="agent.job_title"
                label="Agent's Title"
                component={RenderField}
                validate={[maxLength(100)]}
                disabled={fieldsDisabled}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col md={8} sm={19}>
              <Field
                name="agent.phone_no"
                label="Contact Number"
                required
                validate={
                  isInternational
                    ? [required, maxLength(12)]
                    : [required, phoneNumber, maxLength(12)]
                }
                component={RenderField}
                normalize={normalizePhone}
                disabled={fieldsDisabled}
              />
            </Col>
            <Col md={4} sm={5}>
              <Field
                name="agent.phone_ext"
                validate={[maxLength(4)]}
                label="Ext."
                component={RenderField}
                disabled={fieldsDisabled}
              />
            </Col>
            <Col md={12} sm={24}>
              <Field
                name="agent.email"
                label="Email Address"
                required
                validate={[required, email, maxLength(60)]}
                component={RenderField}
                disabled={fieldsDisabled}
              />
            </Col>
          </Row>

          <Typography.Title level={5}>Mailing Address</Typography.Title>
          <Row gutter={16}>
            <Col md={19} sm={24}>
              <Field
                name="agent.address.address_line_1"
                label="Street"
                required
                validate={[required, maxLength(100)]}
                component={RenderField}
                disabled={fieldsDisabled}
              />
            </Col>
            <Col md={5} sm={24}>
              <Field
                name="agent.address.suite_no"
                label="Unit #"
                component={RenderField}
                validate={[maxLength(5)]}
                disabled={fieldsDisabled}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col md={12} sm={24}>
              <Field
                name="agent.address.address_type_code"
                label="Country"
                required
                validate={[required]}
                data={CONTACTS_COUNTRY_OPTIONS}
                component={RenderSelect}
                disabled={fieldsDisabled}
              />
            </Col>

            <Col md={12} sm={24}>
              <Field
                name="agent.address.sub_division_code"
                label="Province"
                required={!isInternational}
                data={provinceOptions.filter((p) => p.subType === address_type_code)}
                validate={!isInternational ? [required] : []}
                component={RenderSelect}
                disabled={fieldsDisabled}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col md={12} sm={24}>
              <Field
                name="agent.address.city"
                label="City"
                required
                validate={[required]}
                component={RenderField}
                disabled={fieldsDisabled}
              />
            </Col>
            <Col md={12} sm={24}>
              <Field
                name="agent.address.post_code"
                label="Postal Code"
                component={RenderField}
                validate={[postalCodeWithCountry(address_type_code), maxLength(10)]}
                disabled={fieldsDisabled}
              />
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};
